import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef } from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@r/utils/utils.ts';

const labelVariants = cva(
  'block mb-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-30',
  {
    variants: {
      variant: {
        default: 'text-foreground'
      },
      labelSize: {
        default: 'text-sm pl-[0.125rem]',
        sm: 'text-xs pl-[0.063rem]',
        md: 'text-sm pl-[0.125rem]',
        lg: 'text-lg pl-[0.188rem]'
      },
      state: {
        default: '',
        disabled: 'opacity-30 cursor-not-allowed'
      }
    },
    defaultVariants: {
      variant: 'default',
      labelSize: 'default',
      state: 'default'
    }
  }
);

export interface LabelProps
  extends ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    VariantProps<typeof labelVariants> {
  variant?: 'default';
  state?: 'default' | 'disabled';
  labelSize?: 'default' | 'sm' | 'md' | 'lg';
}

const Label = forwardRef<ComponentRef<typeof LabelPrimitive.Root>, LabelProps>(
  ({ className, variant, state, labelSize, ...props }, ref) => (
    <LabelPrimitive.Root
      ref={ref}
      className={cn(labelVariants({ variant, labelSize, state, className }))}
      {...props}
    />
  )
);

Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
